export type ReportDisplayItem = TableDisplay | SeriesChartDisplay | XYChartDisplay | TextDisplay | AISummaryDisplay;

export type DisplayType = 'table' | 'series_chart' | 'xy_chart' | 'text' | 'ai_summary';

export type DisplayShowOn = 'full' | 'embedded' | 'widget';

export interface BaseDisplay {
    id: string;
    type: DisplayType;
    showOn?: DisplayShowOn[];
}

export interface TextDisplay extends BaseDisplay {
    type: 'text';
    content: string;
}

export type AISummaryFormat = 'markdown' | 'text' | 'html';

export interface AISummaryOptions {
    model?: string | null;
    max_rows?: number;
    format?: AISummaryFormat;
    cache?: SourceCache | null;
}

export interface AISummaryDisplay extends BaseDisplay {
    type: 'ai_summary';
    sources: string[];
    prompt: string;
    title?: string;
    description?: string;
    options?: AISummaryOptions;
}

export interface TableOptionsExtra {
    field: string;
    type: 'preview';
    label: string;
    replace: boolean;
}

export interface TableOptions {
    noPagination: boolean;
    showCsvDownload: boolean;
    defaultPrecision?: number;
    size: number;
    cache?: SourceCache | null;
    extraColumns: TableOptionsExtra[];
}

export interface SourceCache {
    cached?: boolean;
    ttl?: number;
    omit_filters?: string[];
}

export interface TableDisplay extends BaseDisplay {
    type: 'table';
    output: string;
    options: TableOptions;
}

export interface SeriesChartOptions {
    minType: 'auto' | 'fixed';
    min?: number;
    maxType: 'auto' | 'fixed';
    max?: number;
}

export interface XYChartOptions {
    xMinType: 'auto' | 'fixed';
    xMin?: number;
    xMaxType: 'auto' | 'fixed';
    xMax?: number;

    yMinType: 'auto' | 'fixed';
    yMin?: number;
    yMaxType: 'auto' | 'fixed';
    yMax?: number;
}

export interface ChartDataPoint {
    xColumn: string;
    yColumn: string;
    label: string;
    color?: string;
}

export interface XYChartDisplay extends BaseDisplay {
    type: 'xy_chart';
    output: string;
    points: ChartDataPoint[];
    horizontalLabel?: string;
    verticalLabel?: string;
    title: string;
    description?: string;
    options: XYChartOptions;
}

export interface ChartVertical {
    column: string;
    label: string;
    color?: string;
}

export interface ChartHorizontal {
    column: string;
}

export type SeriesChartType =
    | 'bar'
    | 'line'
    | 'pie'
    | 'donut'
    | 'radar'
    | 'polar'
    | 'area'
    | 'column'
    | 'stackedColumn'
    | 'stackedBar';

export interface SeriesChartDisplay extends BaseDisplay {
    type: 'series_chart';
    output: string;
    vertical: ChartVertical[];
    horizontal: ChartHorizontal;
    horizontalLabel?: string;
    verticalLabel?: string;
    chartType: SeriesChartType;
    title: string;
    description?: string;
    options: SeriesChartOptions;
}

/**
 * Unique identifier for the filter
 */
export type Id = string
/**
 * Label for the filter
 */
export type Label = string
/**
 * Type of the filter
 */
export type Filtertype = string
/**
 * Initial value
 */
export type Initialvalue = string | number | boolean | unknown[] | null
/**
 * Field to apply the filter on
 */
export type Field = string
/**
 * Extra information for the filter
 */
export type Extra = {
  userField?: string;
  eventTypes?: string[];
  allowedValues?: string | string[];
  extraCategories?: string[];
  blueprints?: string[];
  relations?: string[];
  blueprint?: string;
  relation?: string;
  bookingItems?: string[];
  roles?: string[];
  dateMode?: string;
  relativeDays?: number;
} | null

export interface FilterForm {
  id: Id
  label: Label
  filterType: Filtertype
  initialValue?: Initialvalue
  field: Field
  extra?: Extra
}

export interface RunnableReportConfiguration {
    id: string;
    filterForm: FilterForm[];
    display: ReportDisplayItem[];
}

export function isTableDisplayItem(item: ReportDisplayItem): item is TableDisplay {
    return item.type === 'table';
}

export function isSeriesChartDisplayItem(item: ReportDisplayItem): item is SeriesChartDisplay {
    return item.type === 'series_chart';
}

export function isXYChartDisplayItem(item: ReportDisplayItem): item is XYChartDisplay {
    return item.type === 'xy_chart';
}

export function isTextDisplayItem(item: ReportDisplayItem): item is TextDisplay {
    return item.type === 'text';
}

export function isAISummaryDisplayItem(item: ReportDisplayItem): item is AISummaryDisplay {
    return item.type === 'ai_summary';
}

export interface AISummaryOutput {
    name: string;
    type: 'ai_summary';
    format: AISummaryFormat;
    content: string | null;
    sources: string[];
    model: string;
    cached: boolean;
    error: string | null;
}
